import { type FieldMetadata, useInputControl } from '@conform-to/react'
import {
	type CheckboxProps as RadixCheckboxProps,
	Checkbox as RadixCheckbox,
	Flex,
} from '@radix-ui/themes'
import { type TFunction } from 'i18next'
import * as React from 'react'

import { FormFieldError } from '#app/components/ui/form-field-error.tsx'
import { type LabelProps, Label } from '#app/components/ui/label/label.tsx'

/* -------------------------------------------------------------------------------------------------
 * Checkbox
 * -----------------------------------------------------------------------------------------------*/

type CheckboxElement = React.ElementRef<typeof RadixCheckbox>
type CheckboxProps = React.PropsWithoutRef<RadixCheckboxProps> & {
	children: LabelProps['children']
	t: TFunction<string, undefined>
	meta: FieldMetadata<string | boolean | undefined>
}

const Checkbox = React.forwardRef<CheckboxElement, CheckboxProps>(
	(props, forwardedRef) => {
		const { children, t, meta, ...rest } = props

		const control = useInputControl(meta)

		return (
			<Flex direction="column" gapY="2">
				<Flex align="center" gapX="3">
					<Label htmlFor={meta.id} color="gray" className="order-1 select-none">
						{children}
					</Label>
					<RadixCheckbox
						ref={forwardedRef}
						id={meta.id}
						onCheckedChange={(checked) => {
							control.change(checked ? 'on' : '')
						}}
						checked={(control.value || meta.initialValue) === 'on'}
						onBlur={control.blur}
						size="2"
						{...rest}
					/>
				</Flex>
				{!!meta.errors && <FormFieldError error={meta.errors[0]} t={t} />}
			</Flex>
		)
	},
)
Checkbox.displayName = 'Checkbox'

/* -----------------------------------------------------------------------------------------------*/

export { Checkbox }
